import React from "react";
import PropTypes from "prop-types";
import optimizedArtwork from "../../../assets/images/progressive-bonuses-base.webp";
import {
  formatBonusAmount,
  mainBonusFields,
  progressiveBonusOverlayLayout,
  progressiveBonusDefaults,
  straightFlushFields,
} from "../progressiveBonusConfig";
import "../progressiveBonuses.css";

function ProgressiveBonusesBoard({ bonuses, projection }) {
  const currentBonuses = { ...progressiveBonusDefaults, ...bonuses };
  const overlayStyle = (field) => {
    const { left, top, fontSize } = progressiveBonusOverlayLayout[field];

    return {
      left: `${left}%`,
      top: `${top}%`,
      fontSize: `${fontSize}cqw`,
    };
  };
  const rankLayout = progressiveBonusOverlayLayout.quad_of_day_rank;

  return (
    <section className={`progressive-bonuses-board${projection ? " progressive-bonuses-board--projection" : ""}`}>
      <img
        className="progressive-bonuses-board__artwork"
        src={optimizedArtwork}
        alt="The Royal Flush progressive bonuses artwork"
        width="1536"
        height="1024"
        decoding="async"
      />

      <ol className="progressive-bonuses-board__dynamic-values">
        {mainBonusFields.map((bonus) => (
          <li
            key={bonus.field}
            className={`progressive-bonuses-board__value progressive-bonuses-board__value--${bonus.field}`}
            aria-label={`${bonus.label} bonus`}
            style={overlayStyle(bonus.field)}
          >
            <span className="progressive-bonuses-board__sr-only">{bonus.label}: </span>
            <span>{formatBonusAmount(currentBonuses[bonus.field])}</span>
          </li>
        ))}
        {straightFlushFields.map((bonus) => (
          <li
            key={bonus.field}
            className={`progressive-bonuses-board__value progressive-bonuses-board__value--${bonus.field}`}
            aria-label={`${bonus.label} straight flush bonus`}
            style={overlayStyle(bonus.field)}
          >
            <span className="progressive-bonuses-board__sr-only">{bonus.label}: </span>
            <span>{formatBonusAmount(currentBonuses[bonus.field])}</span>
          </li>
        ))}
      </ol>

      <strong className={`progressive-bonuses-board__rank${
        currentBonuses.quad_of_day_rank === "TBD"
          ? " progressive-bonuses-board__rank--pending"
          : ""
      }`} style={{
        left: `${rankLayout.left}%`,
        top: `${rankLayout.top}%`,
        fontSize: `${currentBonuses.quad_of_day_rank === "TBD"
          ? rankLayout.pendingFontSize
          : rankLayout.fontSize}cqw`,
      }}>
        <span className="progressive-bonuses-board__sr-only">Quad of the Day rank: </span>
        {currentBonuses.quad_of_day_rank}
      </strong>

      <div className="progressive-bonuses-board__sr-only">
        <p>Bomb pots are excluded from all bonuses</p>
        <p>All bonuses need to be flopped in PLO</p>
        <p>Bonuses will increase daily, no cap</p>
        <p>Five active players minimum. Player funded bonuses. No door fee.</p>
      </div>
    </section>
  );
}

ProgressiveBonusesBoard.defaultProps = {
  bonuses: progressiveBonusDefaults,
  projection: false,
};

ProgressiveBonusesBoard.propTypes = {
  bonuses: PropTypes.shape({
    quad_of_day_amount: PropTypes.number,
    four_flushes_amount: PropTypes.number,
    royal_bonus_amount: PropTypes.number,
    bad_beat_amount: PropTypes.number,
    spades_straight_flush_amount: PropTypes.number,
    hearts_straight_flush_amount: PropTypes.number,
    clubs_straight_flush_amount: PropTypes.number,
    diamonds_straight_flush_amount: PropTypes.number,
    quad_of_day_rank: PropTypes.string,
  }),
  projection: PropTypes.bool,
};

export default React.memo(ProgressiveBonusesBoard);
